import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBoddy, DialogFooter, DialogHeader } from '@/components';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { useFolderAncestors, useFolderContents } from '../hooks';
import { getDocumentErrorMessage } from '../utils/documentErrors';
import { DocumentBreadcrumb } from './partials/DocumentBreadcrumb';
import type { DocumentBreadcrumbItem } from '../types';

interface FolderPickerDialogProps {
  open: boolean;
  excludeFolderId?: string;
  initialFolderId?: string | null;
  isSubmitting?: boolean;
  onSelect: (folder: FolderWithName) => void;
  onCancel: () => void;
}


interface FolderWithName {
  id: string | null;
  name: string;
}

export function FolderPickerDialog({
  open,
  excludeFolderId,
  initialFolderId,
  isSubmitting = false,
  onSelect,
  onCancel,
}: FolderPickerDialogProps) {
  const { t } = useTranslation();
  const { companyId } = useCurrentCompany();

  // navStack: undefined = root, string = folder ID
  const [draftNavStack, setDraftNavStack] = useState<
    (FolderWithName | undefined)[] | null
  >(null);

  // selected: undefined = nothing chosen, null = root chosen, string = folder ID
  const [draftSelected, setDraftSelected] = useState<FolderWithName | null | undefined>(
    undefined
  );

  const ancestorsQuery = useFolderAncestors(
    initialFolderId ?? undefined,
    open && Boolean(initialFolderId)
  );

  const initialNavStack = useMemo<(FolderWithName | undefined)[]>(() => {
    if (initialFolderId && ancestorsQuery.data) {
      return [undefined, ...ancestorsQuery.data];
    }

    return [undefined];
  }, [ancestorsQuery.data, initialFolderId]);

  const navStack = draftNavStack ?? initialNavStack;
  const currentFolder = navStack[navStack.length - 1];
  const currentFolderId = currentFolder?.id;
  const selected = draftSelected ?? currentFolder;

  // If nothing is selected, the current folder counts as selected
  const effectiveSelected = draftSelected ?? currentFolder ?? null;

  // Get ancestors for the current folder
  const currentAncestorsQuery = useFolderAncestors(
    currentFolderId ?? undefined,
    open && Boolean(currentFolderId)
  );

  const { data, isLoading, isError, error, refetch } = useFolderContents(
    companyId,
    currentFolderId ?? undefined
  );

  // Build breadcrumb items for current folder
  const breadcrumbItems = useMemo(() => {
    if (!currentFolderId) return [];

    const ancestors = currentAncestorsQuery.data ?? [];

    if (ancestors.some((ancestor) => ancestor.id === currentFolderId)) {
      return ancestors;
    }

    const currentFolderName =
      data?.currentFolder?.id === currentFolderId
        ? data.currentFolder.name
        : t('documents.breadcrumb.currentFolderFallback');

    return [
      ...ancestors,
      {
        id: currentFolderId,
        name: currentFolderName,
      },
    ];
  }, [currentAncestorsQuery.data, currentFolderId, data, t]);

  const handleBreadcrumbClick = (_item: DocumentBreadcrumbItem, index: number) => {
    const targetNavStack = [
      undefined,
      ...breadcrumbItems.slice(0, index + 1).map((crumb) => (crumb)),
    ];
    setDraftNavStack(targetNavStack);
    setDraftSelected(undefined);
  };

  const handleRootBreadcrumbClick = () => {
    setDraftNavStack([undefined]);
    setDraftSelected(undefined);
  };

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setDraftNavStack(null);
      setDraftSelected(undefined);
    }
  }, [open]);

  const visibleFolders = data
    ? data.folders.filter((f) => f.id !== excludeFolderId)
    : [];

  const getSelectedFolder = (): FolderWithName | null => {
    if (effectiveSelected === undefined) {
      return null;
    }

    if (effectiveSelected === null) {
      return {
        id: null,
        name: t('documents.folderPicker.rootLabel'),
      };
    }

    return effectiveSelected;
  };

  const handleSelect = () => {
    const selectedFolder = getSelectedFolder();
    if (selectedFolder) {
      onSelect(selectedFolder);
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onCancel}
    >
      <DialogHeader>{t('documents.folderPicker.title')}</DialogHeader>
      <DialogBoddy className="flex min-h-64 flex-col gap-4 py-4">

        {/* Breadcrumb navigation */}
        <div className="flex flex-col gap-1">
          <div>
            <DocumentBreadcrumb
              rootLabel={t('documents.folderPicker.rootLabel')}
              items={breadcrumbItems}
              onRootClick={handleRootBreadcrumbClick}
              onItemClick={handleBreadcrumbClick}
            />
          </div>
        </div>


        {/* Content area */}
        <div className="flex-1">
          {isLoading && (
            <p className="text-muted py-4 text-center text-sm">
              {t('app.loading')}
            </p>
          )}

          {isError && (
            <div className="flex flex-col items-center gap-2 py-4">
              <p className="text-sm text-red-600">
                {getDocumentErrorMessage(error, import.meta.env.DEV, t)}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-primary text-sm hover:underline"
              >
                {t('documents.folderPicker.retry')}
              </button>
            </div>
          )}

          {!isLoading && !isError && visibleFolders.length === 0 && (
            <p className="text-muted py-4 text-center text-sm">
              {t('documents.folderPicker.emptyFolder')}
            </p>
          )}

          {!isLoading && !isError && visibleFolders.length > 0 && (
            <ul className="flex flex-col gap-1">
              {visibleFolders.map((folder) => (
                <li key={folder.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setDraftSelected({ id: folder.id, name: folder.name })}
                    onDoubleClick={() => {
                      setDraftNavStack((stack) => [
                        ...(stack ?? initialNavStack),
                        { id: folder.id, name: folder.name },
                      ]);
                      setDraftSelected(undefined);
                    }}
                    disabled={isSubmitting}
                    className={[
                      'flex flex-1 items-center rounded-md px-3 py-2 text-left text-sm transition-colors',
                      selected?.id === folder.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-field text-onsurface disabled:opacity-60',
                    ].join(' ')}
                  >
                    {folder.name}
                  </button>
                  <button
                    type="button"
                    aria-label={t('documents.folderPicker.navigateInto')}
                    disabled={isSubmitting}
                    onClick={() => {
                      setDraftNavStack((stack) => [
                        ...(stack ?? initialNavStack),
                        { id: folder.id, name: folder.name },
                      ]);
                      setDraftSelected(undefined);
                    }}
                    className="text-muted hover:text-onsurface p-2 transition-colors disabled:opacity-60"
                  >
                    ›
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

      </DialogBoddy>
      <DialogFooter
        abortText={t('documents.folderPicker.cancel')}
        submitText={isSubmitting ? t('documents.folderPicker.submitting') : t('documents.folderPicker.selectButton')}
        submitButtonProps={{ disabled: isSubmitting }}
        abortButtonProps={{ disabled: isSubmitting }}
        onSubmit={handleSelect}
      />
      {/*   <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-muted hover:text-onsurface px-4 py-2 text-sm font-medium transition-colors"
        >
          {t('documents.folderPicker.cancel')}
        </button>
        <button
          type="button"
          disabled={isSubmitting}
          onClick={handleSelect}
          className="bg-primary text-onprimary hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting
            ? t('documents.folderPicker.submitting')
            : t('documents.folderPicker.selectButton')}
        </button>
      </DialogFooter> */}
    </Dialog>
  );
}
